import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Pill } from "@/components/StatusPill";
import { Loader2, ShieldCheck, KeyRound, CheckCircle2, XCircle, RefreshCw, Send, Lock } from "lucide-react";

const CRED_FIELDS = [
  ["account_sid", "Account SID"],
  ["api_key_sid", "API Key SID"],
  ["api_key_secret", "API Secret"],
  ["auth_token", "Auth Token"],
  ["messaging_service_sid", "Messaging Service SID"],
];

function Present({ ok }) {
  return ok
    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> Configured</span>
    : <span className="inline-flex items-center gap-1 text-rose-500 text-xs font-medium"><XCircle className="w-3.5 h-3.5" /> Missing</span>;
}

export default function CommunicationsSettings() {
  const { can } = useAuth();
  const maySend = can("communications.send");

  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [settings, setSettings] = useState(null);
  const [creds, setCreds] = useState({ account_sid: "", api_key_sid: "", api_key_secret: "", auth_token: "", messaging_service_sid: "" });
  const [fromNumber, setFromNumber] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [queued, setQueued] = useState([]);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, s, q] = await Promise.all([
        api.get("/communications/config-check"),
        api.get("/communications/settings"),
        api.get("/communications/queued"),
      ]);
      setCfg(c.data);
      setSettings(s.data);
      setFromNumber(s.data.from_number || "");
      setEnabled(!!s.data.enabled);
      setQueued(q.data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveCreds = async () => {
    const payload = Object.fromEntries(Object.entries(creds).filter(([, v]) => (v || "").trim()));
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one credential to update"); return; }
    setSavingCreds(true);
    try {
      await api.put("/communications/providers/credentials", { provider: "twilio", credentials: { ...payload } });
      toast.success("Credentials saved (encrypted at rest)");
      setCreds({ account_sid: "", api_key_sid: "", api_key_secret: "", auth_token: "", messaging_service_sid: "" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingCreds(false); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/communications/settings", { provider: "twilio", from_number: fromNumber.trim() || null, enabled });
      toast.success("Settings saved");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const sendQueued = async (m) => {
    setSendingId(m.id);
    try {
      const r = await api.post(`/communications/messages/${m.id}/retry`);
      if (r.data.dispatched) toast.success("Message sent");
      else toast.message(r.data.error || r.data.note || "Not sent (live sending gated off)");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingId(null); }
  };

  const [validating, setValidating] = useState(false);
  const [validateRes, setValidateRes] = useState(null);
  const validate = async () => {
    setValidating(true); setValidateRes(null);
    try {
      const r = await api.post("/communications/validate");
      setValidateRes(r.data);
      if (r.data.ok) toast.success("Twilio credentials are valid");
      else toast.error("Validation failed — check the credentials");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setValidating(false); }
  };

  if (loading) return <div className="p-10 flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-4xl space-y-6" data-testid="comms-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><KeyRound className="w-6 h-6 text-indigo-500" /> SMS Provider Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Configure this company's Twilio account. Secrets are encrypted at rest and never displayed again after saving.</p>
      </div>

      {/* Readiness */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="comms-config-check">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-indigo-500" /> Activation readiness</h2>
          <Button variant="outline" size="sm" onClick={load} className="gap-1.5"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          {CRED_FIELDS.map(([k, label]) => (
            <div key={k} className="flex items-center justify-between border-b border-slate-50 py-1.5" data-testid={`cfg-${k}`}>
              <span className="text-slate-600">{label}</span><Present ok={cfg?.fields?.[k]} />
            </div>
          ))}
          <div className="flex items-center justify-between border-b border-slate-50 py-1.5" data-testid="cfg-from_number">
            <span className="text-slate-600">Sender number</span><Present ok={cfg?.fields?.from_number} />
          </div>
          <div className="flex items-center justify-between border-b border-slate-50 py-1.5" data-testid="cfg-encryption">
            <span className="text-slate-600">At-rest encryption</span><Present ok={cfg?.encryption_configured} />
          </div>
          <div className="flex items-center justify-between border-b border-slate-50 py-1.5" data-testid="cfg-route">
            <span className="text-slate-600">Number routed to this company</span><Present ok={cfg?.number_route_registered} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4">
          <span className="text-sm text-slate-600">Global switch:</span>
          <Pill colour={cfg?.global_send_enabled ? "green" : "grey"} dot>{cfg?.global_send_enabled ? "ON" : "OFF"}</Pill>
          <span className="text-sm text-slate-600 ml-2">Company enabled:</span>
          <Pill colour={cfg?.company_enabled ? "green" : "grey"} dot>{cfg?.company_enabled ? "ON" : "OFF"}</Pill>
          <span className="text-sm text-slate-600 ml-2">Live sending:</span>
          <Pill colour={cfg?.live_sending_active ? "green" : "amber"} dot data-testid="cfg-live-status">{cfg?.live_sending_active ? "ACTIVE" : "OFF"}</Pill>
          <span className="ml-auto text-sm font-medium" data-testid="cfg-ready">{cfg?.ready_to_activate ? <span className="text-emerald-600">Ready to activate</span> : <span className="text-amber-600">Not ready</span>}</span>
        </div>
        {(cfg?.webhook_inbound_url || cfg?.webhook_status_url) && (
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-2" data-testid="comms-webhook-urls">
            <p className="text-xs font-semibold text-slate-600">Twilio webhook URLs — set these on your Messaging Service:</p>
            <div className="text-[12px] text-slate-500 break-all"><span className="font-medium text-slate-700">Inbound SMS:</span> <code className="bg-slate-50 px-1.5 py-0.5 rounded" data-testid="webhook-inbound">{cfg.webhook_inbound_url}</code></div>
            <div className="text-[12px] text-slate-500 break-all"><span className="font-medium text-slate-700">Delivery status:</span> <code className="bg-slate-50 px-1.5 py-0.5 rounded" data-testid="webhook-status">{cfg.webhook_status_url}</code></div>
          </div>
        )}
      </section>

      {/* Credentials */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="comms-creds-form">
        <h2 className="text-base font-bold text-slate-900 mb-1">Twilio credentials</h2>
        <p className="text-xs text-slate-400 mb-4 flex items-center gap-1"><Lock className="w-3 h-3" /> Values are write-only. Leave a field blank to keep the stored value.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CRED_FIELDS.map(([k, label]) => (
            <div key={k}>
              <label className="text-xs font-medium text-slate-600">{label}</label>
              <Input type={k.includes("secret") || k === "auth_token" ? "password" : "text"} value={creds[k]}
                onChange={(e) => setCreds((s) => ({ ...s, [k]: e.target.value }))}
                placeholder={cfg?.fields?.[k] ? "•••••••• (configured)" : `Enter ${label}`}
                data-testid={`cred-${k}`} className="mt-1" autoComplete="off" />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
          {validateRes && (
            <span className={`text-xs font-medium mr-auto ${validateRes.ok ? "text-emerald-600" : "text-rose-600"}`} data-testid="validate-result">
              {validateRes.ok
                ? `Valid — credentials can send${validateRes.checks?.messaging_service ? " · messaging service OK" : (validateRes.checks?.messaging_service === false ? " · messaging service not readable (send still works)" : "")}`
                : `Invalid: ${validateRes.error || validateRes.checks?.errors?.messages_list || "check failed"}`}
            </span>
          )}
          <Button variant="outline" className="gap-1.5" disabled={validating || !cfg?.fields?.account_sid} onClick={validate} data-testid="cred-validate">
            {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Validate credentials
          </Button>
          <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={savingCreds} onClick={saveCreds} data-testid="cred-save">
            {savingCreds ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Save credentials
          </Button>
        </div>
      </section>

      {/* Sender + enable */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="comms-sender-form">
        <h2 className="text-base font-bold text-slate-900 mb-4">Sender number & activation</h2>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="flex-1">
            <label className="text-xs font-medium text-slate-600">Sender / receiving number (E.164)</label>
            <Input value={fromNumber} onChange={(e) => setFromNumber(e.target.value)} placeholder="+441134960000" data-testid="settings-from-number" className="mt-1" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 pb-2">
            Enable this company <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="settings-enabled" />
          </label>
          <Button className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white pb-2 sm:pb-0" disabled={savingSettings} onClick={saveSettings} data-testid="settings-save">
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-3">Live SMS only sends when BOTH the global switch (set by an operator via env) AND this company toggle are ON, with credentials present. Enabling this company alone does not send anything while the global switch is OFF.</p>
      </section>

      {/* Queued review */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="comms-queued">
        <h2 className="text-base font-bold text-slate-900 mb-1">Queued messages ({queued.length})</h2>
        <p className="text-xs text-slate-400 mb-4">Created while live sending was disabled. They are never sent automatically — review and send each explicitly.</p>
        {queued.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-4" data-testid="comms-queued-empty">No queued messages.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {queued.map((m) => (
              <div key={m.id} className="py-3 flex items-center gap-3" data-testid={`queued-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 truncate">{m.body}</div>
                  <div className="text-[11px] text-slate-400">To {m.to_number} · {new Date(m.created_at).toLocaleString("en-GB")}</div>
                </div>
                {maySend && (
                  <Button size="sm" variant="outline" className="gap-1.5" disabled={sendingId === m.id} onClick={() => sendQueued(m)} data-testid={`queued-send-${m.id}`}>
                    {sendingId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send now
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
